import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/apiConfig';
import { FlagIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import GoalForm from './forms/GoalForm';
import { Link } from 'react-router-dom';
import { Button, Alert, Spinner, Badge } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

// Interface for Goal data
interface Goal {
  id: number;
  title: string;
  description?: string;
  status?: string; // e.g., 'active', 'achieved', 'on_hold'
  target_date?: string | null;
  // Add other fields like progress, related_projects, etc.
}

const GoalList: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/goals/');
      setGoals(response.data);
    } catch (error) {
      console.error('Error fetching goals:', error);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  const handleEdit = (goal: Goal) => {
    setSelectedGoal(goal);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (goalId: number) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;

    try {
      const response = await apiClient.delete(`/api/goals/${goalId}/`);

      if (response.status >= 200 && response.status < 300) {
        setGoals(goals.filter(goal => goal.id !== goalId));
      } else {
        throw new Error(`Failed to delete goal (${response.status})`);
      }
    } catch (err) {
      console.error("Error deleting goal:", err);
      setDeleteError("Failed to delete goal. Please try again.");
      setTimeout(() => setDeleteError(null), 3000);
    }
  };

  const handleGoalUpdated = (updatedGoal: Goal) => {
    setGoals(goals.map(goal => goal.id === updatedGoal.id ? updatedGoal : goal));
    setIsEditModalOpen(false);
    setSelectedGoal(null);
  };

  const getStatusVariant = (status?: string): 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case 'active': return 'info';
      case 'achieved': return 'success';
      case 'on_hold': return 'warning';
      case 'planning': return 'primary';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return null;
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return "Invalid Date";
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}

      {deleteError && (
        <Alert variant="error">
          {deleteError}
        </Alert>
      )}

      {goals.length === 0 && !error && !loading && (
        <p style={{
          textAlign: 'center',
          color: COLORS.text.secondary,
          paddingTop: SPACING[16],
          paddingBottom: SPACING[16],
          fontSize: TYPOGRAPHY.body.sizes.default
        }}>No goals found.</p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: SPACING[16]
      }}>
        {goals.map((goal) => (
          <Link
            to={`/goals/${goal.id}`}
            key={goal.id}
            style={{
              display: 'block',
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              padding: SPACING[16],
              textDecoration: 'none',
              color: 'inherit',
              transition: 'box-shadow 150ms ease'
            }}
            tabIndex={0}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING[12]
              }}>
                <div style={{
                  padding: SPACING[8],
                  backgroundColor: `${COLORS.accent.default}10`,
                  borderRadius: '50%'
                }}>
                  <FlagIcon style={{
                    width: '20px',
                    height: '20px',
                    color: COLORS.accent.default
                  }} />
                </div>
                <div>
                  <h3 style={{
                    fontSize: TYPOGRAPHY.size.base,
                    fontWeight: TYPOGRAPHY.weight.semibold,
                    color: COLORS.text.primary,
                    margin: 0
                  }}>{goal.title}</h3>
                  {goal.status && (
                    <Badge variant={getStatusVariant(goal.status)} size="sm" style={{ marginTop: SPACING[4] }}>
                      {goal.status.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: SPACING[8] }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => { e.preventDefault(); handleEdit(goal); }}
                  title="Edit goal"
                  style={{
                    padding: SPACING[4],
                    height: 'auto',
                    color: COLORS.text.tertiary
                  }}
                >
                  <PencilIcon style={{ width: '16px', height: '16px' }} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => { e.preventDefault(); handleDelete(goal.id); }}
                  title="Delete goal"
                  style={{
                    padding: SPACING[4],
                    height: 'auto',
                    color: COLORS.text.tertiary
                  }}
                >
                  <TrashIcon style={{ width: '16px', height: '16px' }} />
                </Button>
              </div>
            </div>

            {goal.description && (
              <p style={{
                marginTop: SPACING[8],
                fontSize: TYPOGRAPHY.body.sizes.small,
                color: COLORS.text.secondary,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical'
              }}>{goal.description}</p>
            )}

            {goal.target_date && (
              <p style={{
                fontSize: TYPOGRAPHY.body.sizes.tiny,
                color: COLORS.text.tertiary,
                marginTop: SPACING[8],
                textAlign: 'right'
              }}>
                Target: {formatDate(goal.target_date)}
              </p>
            )}
          </Link>
        ))}
      </div>

      {selectedGoal && (
        <GoalForm
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedGoal(null);
          }}
          onGoalCreated={handleGoalUpdated}
          initialGoal={selectedGoal}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default GoalList;
